import React from "react";
import { connect } from "react-redux";
import * as tree from "state/actions/tree";
import { moveComponent, createComponent } from "cms/state/actions/cms";
import cn from "classnames";
import { selectList } from "state/selectors/lists";
import FileExplorerTheme from "react-sortable-tree-theme-full-node-drag";
import Menu from "@material-ui/core/Menu";
import { SortableTreeWithoutDndContext as SortableTree } from "react-sortable-tree";
import ComponentContextMenu from "./ComponentContextMenu";

import "react-sortable-tree/style.css";
import { typeToField } from "cms/state/selectors/cms";
import Icon from "components/Icon";
import icons from "config/icons";

import "./style.css";
import SectionsStack from "./SectionsStack";
import NodeFlags from "./NodeFlags";

export const canHaveChildren = [
    "section",
    "layout",
    "structured",
    "list",
    "card",
    "form",
    "form-select",
    "context",
    "geo",
    "query",
    "plan",
    "link",
];

const contextMenuOrigin = {
    horizontal: "left",
    vertical: "bottom",
};

function findParentNode(data, id, parent) {
    for (let i = 0; i < data.length; i++) {
        if (data[i].id === id) {
            return { id: parent, children: data.map((node) => node.id) };
        } else {
            let result = findParentNode(data[i].children, id, data[i].id);
            if (result) return result;
        }
    }
    return null;
}

function calculateMoveNode({ type, id, data }, args) {
    let node = args.node || {};
    let nextParent = findParentNode(args.treeData, node.id, null);
    let prevParent = findParentNode(data, node.id, null);
    prevParent.children = prevParent.children.filter((id) => id !== node.id);
    if (!prevParent) return null;
    if (!nextParent) return null;
    let prevType = prevParent.id ? "cms.components" : type;
    let prevUpdate = {};
    prevUpdate[typeToField(prevType)] = prevParent.children;
    let prev = {
        type: prevType,
        id: prevParent.id ? prevParent.id : id,
        update: prevUpdate,
    };
    let nextType = nextParent.id ? "cms.components" : type;
    let nextUpdate = {};
    nextUpdate[typeToField(nextType)] = nextParent.children;
    let next = {
        type: nextType,
        id: nextParent.id ? nextParent.id : id,
        update: nextUpdate,
    };
    return {
        prev,
        next,
    };
}

const mapStateToProps = (store, props) => {
    return {
        data: props.tree,
        filter: selectList(store, props.type, [props.list, "filter"].join("/")),
    };
};

const mapDispatchToProps = {
    onExpand: tree.expand,
    onCollapse: tree.collapse,
    onChange: moveComponent,
    onCreate: createComponent,
};

function ComponentTreePlaceholder(props) {
    return (
        <div className={"w-fill pad-sm rows rows-center o-50"}>
            No components
        </div>
    );
}

class ComponentTree extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = { contextMenu: false, contextMenuAnchor: null };
    }

    generateNodeProps = ({ node, isSearchMatch }) => {
        return {
            title: this.nodeTitle(node, isSearchMatch),
            selected: this.props.focusId === node.id,
        };
    };

    showContextMenu = (evt, id, type) => {
        evt.preventDefault();
        this.props.onFocus(type || "cms.components", id);
        this.setState({
            contextMenu: id,
            contextMenuType: type || "cms.components",
            contextMenuAnchor: evt.target,
        });
    };

    hideContextMenu = (id) => {
        this.setState({ contextMenu: false, contextMenuType: null });
    };

    nodeTitle = (node, match) => {
        const icon = icons["cms.components." + node.type];
        return (
            <div
                className={cn("rstcustom__rowTitleContent", {
                    "is-focused": this.props.focusId === node.id,
                    "is-disabled": !!node.disabled,
                })}
                onClick={() => this.handleClick("cms.components", node.id)}
                onContextMenu={(evt) => this.showContextMenu(evt, node.id)}
                onDoubleClick={() => this.props.onEdit(true)}
            >
                <div className={"rstcustom__rowTitleLabel"}>
                    {icon ? (
                        <Icon defaultType={"mui"} className={"icon"}>
                            {icon}
                        </Icon>
                    ) : null}
                    {!node.label && (
                        <span className={"new-component-label"}>
                            new component
                        </span>
                    )}
                    {node.label && <span>{node.label}</span>}
                </div>
                <div className={"rstcustom__rowTitleActions"}>
                    <NodeFlags node={node} />
                </div>
            </div>
        );
    };

    handleClick = (type, id) => {
        const treeType = this.props.type;
        this.props.onExpand(treeType, this.props.id, id);
        this.props.onClick(type, id);
    };

    canDrag = ({ node }) => {
        return true;
        //if (!node.parent) return false;
    };

    canDrop = ({ node, nextParent }) => {
        if (!nextParent) return true;
        return canHaveChildren.indexOf(nextParent.type) > -1;
    };

    canNodeHaveChildren = (node) => {
        if (!node) return true;
        return canHaveChildren.indexOf(node.type) > -1;
    };

    handleMoveNode = (args) => {
        const { onChange } = this.props;
        const update = calculateMoveNode(this.props, args);
        if (!update) return;
        onChange(update.prev, update.next);
    };

    handleCreate = (type) => {
        const { onCreate, focusType, focusId } = this.props;
        onCreate(focusType, focusId, typeToField(focusType), {
            element_type: type,
        });
    };

    search = ({ node, searchQuery }) => {
        if (!searchQuery.length) return false;
        return searchQuery.indexOf(node.id) > -1;
    };

    render() {
        const { type, id, data } = this.props;
        const { onVisibilityToggle } = this.props;

        const { contextMenu, contextMenuType, contextMenuAnchor } = this.state;

        if (!data) return <div>Tree data not available</div>;

        return (
            <>
                <SectionsStack
                    id={this.props.section}
                    onFocus={this.props.onFocus}
                    onEdit={this.props.onEdit}
                    treeId={this.props.id}
                    onMenu={this.showContextMenu}
                    focusId={
                        this.props.focusType === "cms.sections"
                            ? this.props.focusId
                            : null
                    }
                />
                {data ? (
                    <SortableTree
                        scaffoldBlockPxWidth={14}
                        theme={FileExplorerTheme}
                        onlyExpandSearchedNodes={false}
                        rowHeight={24}
                        treeData={data}
                        canDrag={this.canDrag}
                        canDrop={this.canDrop}
                        canNodeHaveChildren={this.canNodeHaveChildren}
                        onChange={() => null}
                        onVisibilityToggle={onVisibilityToggle}
                        onMoveNode={this.handleMoveNode}
                        generateNodeProps={this.generateNodeProps}
                        placeholderRenderer={ComponentTreePlaceholder}
                    />
                ) : null}
                <Menu
                    open={!!contextMenu}
                    anchorEl={contextMenuAnchor}
                    anchorOrigin={contextMenuOrigin}
                    onClose={this.hideContextMenu}
                    className={"style-ui-popover"}
                >
                    <ComponentContextMenu
                        type={contextMenuType}
                        id={contextMenu}
                        rootId={id}
                        rootType={type}
                        onClose={this.hideContextMenu}
                        onEdit={this.props.onEdit}
                        onFocus={this.props.onFocus}
                    />
                </Menu>
            </>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(ComponentTree);
